export interface QuestionOption {
  value: string;
  label: string;
  points: number;
}

export interface ScreeningQuestion {
  id: string;
  text: string;
  type: 'radio';
  required: boolean;
  options: QuestionOption[];
  excludeFromScore?: boolean;
}

export interface ScreeningQuestions {
  it: ScreeningQuestion[];
  en: ScreeningQuestion[];
  es: ScreeningQuestion[];
}

// CONFIGURAZIONE - 17 domande calcolate (q18 esclusa - solo info)
export const TOTAL_QUESTIONS = 17;
export const MAX_SCORE = 170;
export const TIME_LIMIT_SECONDS = 35 * 60; // 35 minuti
export const SUSPICIOUS_ANSWER_TIME = 5; // secondi

// SOGLIE (su 170 punti totali)
export const THRESHOLDS = {
  EXCELLENT: 153,       // 90-100% - badge ORO, approvazione automatica
  APPROVED: 128,        // 75-89% - badge ARGENTO, approvazione automatica
  PENDING_REVIEW: 108,  // 63-74% - revisione manuale
  // sotto 108: ON_HOLD - retry 30 giorni
};

export const RETRY_DAYS = {
  DEFAULT: 30, // unico valore per tutti i non approvati
};

// Legacy exports for backward compatibility
export const PASSING_SCORE = THRESHOLDS.APPROVED;
export const AUTO_APPROVE_SCORE = THRESHOLDS.APPROVED;

export const screeningQuestions: ScreeningQuestions = {
  it: [
    {
      id: 'q1',
      text: 'Nel tuo materiale promozionale, come ti rivolgi ai potenziali clienti?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Uso il maschile sovraesteso come standard grammaticale', points: 0 },
        { value: 'b', label: 'Alterno maschile e femminile nei vari contenuti', points: 5 },
        { value: 'c', label: 'Uso formule neutre quando possibile', points: 10 },
        { value: 'd', label: 'Mi rivolgo sempre al maschile per semplicità', points: 0 }
      ]
    },
    {
      id: 'q2',
      text: 'Un cliente ti mostra documenti in una lingua che non conosci. Come procedi?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Chiedo documenti italiani o tradotti ufficialmente', points: 0 },
        { value: 'b', label: 'Cerco di capire insieme usando traduttori online', points: 10 },
        { value: 'c', label: 'Gli consiglio di tornare con qualcuno che traduca', points: 5 },
        { value: 'd', label: 'Rifiuto il servizio per problemi burocratici', points: 0 }
      ]
    },
    {
      id: 'q3',
      text: 'Stai selezionando immagini per il tuo sito web professionale. Quali scegli?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Le migliori foto professionali disponibili', points: 5 },
        { value: 'b', label: 'Immagini che rappresentano vari tipi di persone', points: 10 },
        { value: 'c', label: 'Foto coerenti con il mio mercato di riferimento', points: 0 },
        { value: 'd', label: 'Preferisco grafiche e icone senza persone', points: 0 }
      ]
    },
    {
      id: 'q4',
      text: 'Un cliente fa errori grammaticali evidenti nelle email. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Correggo gentilmente per aiutarlo', points: 0 },
        { value: 'b', label: 'Rispondo normalmente senza far notare nulla', points: 10 },
        { value: 'c', label: 'Semplifico il mio linguaggio per adeguarmi', points: 5 },
        { value: 'd', label: 'Valuto se è affidabile professionalmente', points: 0 }
      ]
    },
    {
      id: 'q5',
      text: 'Un cliente ti chiede se puoi fornire il servizio in formato accessibile. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Spiego che uso formati standard del settore', points: 0 },
        { value: 'b', label: 'Chiedo di quale formato specifico ha bisogno', points: 10 },
        { value: 'c', label: 'Propongo un sovrapprezzo per il lavoro extra', points: 5 },
        { value: 'd', label: 'Suggerisco servizi specializzati in accessibilità', points: 0 }
      ]
    },
    {
      id: 'q6',
      text: 'Durante una consulenza, un uomo menziona casualmente suo marito. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Proseguo normalmente con la consulenza', points: 10 },
        { value: 'b', label: 'Chiedo conferma per essere sicuro di aver capito bene', points: 0 },
        { value: 'c', label: 'Prendo nota mentalmente per future comunicazioni', points: 5 },
        { value: 'd', label: 'Mi complimento per la bella relazione', points: 0 }
      ]
    },
    {
      id: 'q7',
      text: 'Un cliente ti avverte che potrebbe avere bisogno di pause frequenti durante l\'incontro. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Chiedo se c\'è un problema di salute', points: 0 },
        { value: 'b', label: 'Programmo l\'incontro con margine di tempo extra', points: 10 },
        { value: 'c', label: 'Propongo un incontro più breve', points: 5 },
        { value: 'd', label: 'Suggerisco di rimandare quando sta meglio', points: 0 }
      ]
    },
    {
      id: 'q8',
      text: 'Un collaboratore condivide di attraversare un periodo difficile emotivamente. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Gli consiglio di prendersi una pausa dal lavoro', points: 0 },
        { value: 'b', label: 'Chiedo come posso adattare il carico di lavoro', points: 10 },
        { value: 'c', label: 'Lo incoraggio a essere forte e professionale', points: 0 },
        { value: 'd', label: 'Suggerisco risorse di supporto professionale', points: 5 }
      ]
    },
    {
      id: 'q9',
      text: 'Per un evento aziendale di networking in piedi, quale allestimento scegli?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Tavoli alti eleganti tipo cocktail party', points: 0 },
        { value: 'b', label: 'Mix di sedute alte e basse, tavoli e aree lounge', points: 10 },
        { value: 'c', label: 'Standing reception per favorire il movimento', points: 0 },
        { value: 'd', label: 'Disposizione tradizionale con posti assegnati', points: 5 }
      ]
    },
    {
      id: 'q10',
      text: 'Un potenziale cliente non può fornire un indirizzo di residenza stabile. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Chiedo se può usare un indirizzo di domiciliazione', points: 10 },
        { value: 'b', label: 'Propongo di usare il mio indirizzo professionale', points: 5 },
        { value: 'c', label: 'Verifico alternative legali per la fatturazione', points: 10 },
        { value: 'd', label: 'Suggerisco di regolarizzare prima la situazione', points: 0 }
      ]
    },
    {
      id: 'q11',
      text: 'Come strutturi le tue tariffe professionali?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Prezzo fisso uguale per tutti', points: 0 },
        { value: 'b', label: 'Ho opzioni di prezzo diverse per esigenze diverse', points: 10 },
        { value: 'c', label: 'Tariffe premium per clienti facoltosi', points: 5 },
        { value: 'd', label: 'Il minimo per coprire i costi', points: 0 }
      ]
    },
    {
      id: 'q12',
      text: 'Durante una presentazione, noti che qualcuno sembra non seguire bene. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Continuo normalmente, hanno scelto di partecipare', points: 0 },
        { value: 'b', label: 'Rallento e uso un linguaggio più semplice', points: 10 },
        { value: 'c', label: 'Chiedo se tutti stanno seguendo', points: 5 },
        { value: 'd', label: 'Ignoro e procedo con chi segue attivamente', points: 0 }
      ]
    },
    {
      id: 'q13',
      text: 'Un cliente ultrasettantenne ti contatta per servizi digitali. Pensi:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Dovrò semplificare molto le spiegazioni', points: 0 },
        { value: 'b', label: 'Verifico prima il suo livello di competenza', points: 10 },
        { value: 'c', label: 'Propongo un pacchetto base per principianti', points: 5 },
        { value: 'd', label: 'Mi preparo a ripetere tutto più volte', points: 0 }
      ]
    },
    {
      id: 'q14',
      text: 'Un cliente ti chiede di spostare un appuntamento per una festività religiosa. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Verifico la mia disponibilità e riorganizzo', points: 10 },
        { value: 'b', label: 'Propongo alternative via telefono o video', points: 5 },
        { value: 'c', label: 'Chiedo maggiori dettagli sulla festività per capire', points: 0 },
        { value: 'd', label: 'Accetto applicando la policy per cambi last-minute', points: 0 }
      ]
    },
    {
      id: 'q15',
      text: 'Durante una consulenza scopri che il cliente ha una struttura familiare non convenzionale. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Adatto le mie comunicazioni alla situazione specifica', points: 10 },
        { value: 'b', label: 'Mi attengo strettamente all\'aspetto professionale', points: 5 },
        { value: 'c', label: 'Faccio domande per comprendere meglio le dinamiche', points: 0 },
        { value: 'd', label: 'Uso termini generici per evitare errori', points: 0 }
      ]
    },
    {
      id: 'q16',
      text: 'Un cliente da una zona rurale remota ti contatta. Tu:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Propongo solo servizi online per praticità', points: 5 },
        { value: 'b', label: 'Valuto insieme la soluzione migliore per entrambi', points: 10 },
        { value: 'c', label: 'Applico supplemento per distanza', points: 0 },
        { value: 'd', label: 'Suggerisco professionisti più vicini', points: 0 }
      ]
    },
    {
      id: 'q17',
      text: 'Un giovane professionista ti propone una collaborazione innovativa. La tua prima reazione:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Valuto l\'idea indipendentemente dall\'età', points: 10 },
        { value: 'b', label: 'Chiedo referenze per verificare l\'esperienza', points: 5 },
        { value: 'c', label: 'Sono scettico data la poca esperienza', points: 0 },
        { value: 'd', label: 'Propongo di iniziare con progetti piccoli', points: 0 }
      ]
    },
    {
      id: 'q18',
      text: 'In quale ambito vorresti maggiore formazione?',
      type: 'radio',
      required: true,
      excludeFromScore: true,
      options: [
        { value: 'a', label: 'Competenze tecniche avanzate del mio settore', points: 0 },
        { value: 'b', label: 'Comunicazione inclusiva e gestione della diversità', points: 0 },
        { value: 'c', label: 'Marketing e acquisizione clienti', points: 0 },
        { value: 'd', label: 'Gestione amministrativa e fiscale', points: 0 }
      ]
    }
  ],
  en: [
    {
      id: 'q1',
      text: 'In your promotional material, how do you address potential clients?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I use the masculine overextended as grammatical standard', points: 0 },
        { value: 'b', label: 'I alternate masculine and feminine in various contents', points: 5 },
        { value: 'c', label: 'I use neutral formulas when possible', points: 10 },
        { value: 'd', label: 'I always address in masculine for simplicity', points: 0 }
      ]
    },
    {
      id: 'q2',
      text: 'A client shows you documents in a language you don\'t know. How do you proceed?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I ask for Italian or officially translated documents', points: 0 },
        { value: 'b', label: 'I try to understand together using online translators', points: 10 },
        { value: 'c', label: 'I advise them to come back with someone who can translate', points: 5 },
        { value: 'd', label: 'I refuse the service for bureaucratic problems', points: 0 }
      ]
    },
    {
      id: 'q3',
      text: 'You are selecting images for your professional website. Which do you choose?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'The best professional photos available', points: 5 },
        { value: 'b', label: 'Images that represent various types of people', points: 10 },
        { value: 'c', label: 'Photos consistent with my target market', points: 0 },
        { value: 'd', label: 'I prefer graphics and icons without people', points: 0 }
      ]
    },
    {
      id: 'q4',
      text: 'A client makes obvious grammatical errors in emails. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I gently correct to help them', points: 0 },
        { value: 'b', label: 'I respond normally without pointing out anything', points: 10 },
        { value: 'c', label: 'I simplify my language to adapt', points: 5 },
        { value: 'd', label: 'I evaluate if they are professionally reliable', points: 0 }
      ]
    },
    {
      id: 'q5',
      text: 'A client asks if you can provide the service in accessible format. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I explain that I use industry standard formats', points: 0 },
        { value: 'b', label: 'I ask which specific format they need', points: 10 },
        { value: 'c', label: 'I propose a surcharge for the extra work', points: 5 },
        { value: 'd', label: 'I suggest specialized accessibility services', points: 0 }
      ]
    },
    {
      id: 'q6',
      text: 'During a consultation, a man casually mentions his husband. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I continue normally with the consultation', points: 10 },
        { value: 'b', label: 'I ask for confirmation to make sure I understood correctly', points: 0 },
        { value: 'c', label: 'I mentally note for future communications', points: 5 },
        { value: 'd', label: 'I compliment them on the nice relationship', points: 0 }
      ]
    },
    {
      id: 'q7',
      text: 'A client warns you that they might need frequent breaks during the meeting. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I ask if there is a health problem', points: 0 },
        { value: 'b', label: 'I schedule the meeting with extra time margin', points: 10 },
        { value: 'c', label: 'I propose a shorter meeting', points: 5 },
        { value: 'd', label: 'I suggest rescheduling when they feel better', points: 0 }
      ]
    },
    {
      id: 'q8',
      text: 'A collaborator shares that they are going through a difficult emotional period. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I advise them to take a break from work', points: 0 },
        { value: 'b', label: 'I ask how I can adapt the workload', points: 10 },
        { value: 'c', label: 'I encourage them to be strong and professional', points: 0 },
        { value: 'd', label: 'I suggest professional support resources', points: 5 }
      ]
    },
    {
      id: 'q9',
      text: 'For a standing corporate networking event, which setup do you choose?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Elegant high tables cocktail party style', points: 0 },
        { value: 'b', label: 'Mix of high and low seating, tables and lounge areas', points: 10 },
        { value: 'c', label: 'Standing reception to encourage movement', points: 0 },
        { value: 'd', label: 'Traditional arrangement with assigned seats', points: 5 }
      ]
    },
    {
      id: 'q10',
      text: 'A potential client cannot provide a stable residence address. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I ask if they can use a domiciliation address', points: 10 },
        { value: 'b', label: 'I propose using my professional address', points: 5 },
        { value: 'c', label: 'I check legal alternatives for invoicing', points: 10 },
        { value: 'd', label: 'I suggest regularizing the situation first', points: 0 }
      ]
    },
    {
      id: 'q11',
      text: 'How do you structure your professional rates?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Fixed price the same for everyone', points: 0 },
        { value: 'b', label: 'I have different price options for different needs', points: 10 },
        { value: 'c', label: 'Premium rates for wealthy clients', points: 5 },
        { value: 'd', label: 'The minimum to cover costs', points: 0 }
      ]
    },
    {
      id: 'q12',
      text: 'During a presentation, you notice someone seems not to be following well. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I continue normally, they chose to participate', points: 0 },
        { value: 'b', label: 'I slow down and use simpler language', points: 10 },
        { value: 'c', label: 'I ask if everyone is following', points: 5 },
        { value: 'd', label: 'I ignore and proceed with those actively following', points: 0 }
      ]
    },
    {
      id: 'q13',
      text: 'A client over seventy contacts you for digital services. You think:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I will have to simplify explanations a lot', points: 0 },
        { value: 'b', label: 'I first check their level of competence', points: 10 },
        { value: 'c', label: 'I propose a basic package for beginners', points: 5 },
        { value: 'd', label: 'I prepare to repeat everything multiple times', points: 0 }
      ]
    },
    {
      id: 'q14',
      text: 'A client asks to move an appointment for a religious holiday. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I check my availability and reorganize', points: 10 },
        { value: 'b', label: 'I propose alternatives via phone or video', points: 5 },
        { value: 'c', label: 'I ask for more details about the holiday to understand', points: 0 },
        { value: 'd', label: 'I accept applying the last-minute change policy', points: 0 }
      ]
    },
    {
      id: 'q15',
      text: 'During a consultation you discover the client has an unconventional family structure. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I adapt my communications to the specific situation', points: 10 },
        { value: 'b', label: 'I stick strictly to the professional aspect', points: 5 },
        { value: 'c', label: 'I ask questions to better understand the dynamics', points: 0 },
        { value: 'd', label: 'I use generic terms to avoid mistakes', points: 0 }
      ]
    },
    {
      id: 'q16',
      text: 'A client from a remote rural area contacts you. You:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I propose only online services for convenience', points: 5 },
        { value: 'b', label: 'I evaluate together the best solution for both', points: 10 },
        { value: 'c', label: 'I apply a distance surcharge', points: 0 },
        { value: 'd', label: 'I suggest professionals closer to them', points: 0 }
      ]
    },
    {
      id: 'q17',
      text: 'A young professional proposes an innovative collaboration. Your first reaction:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'I evaluate the idea regardless of age', points: 10 },
        { value: 'b', label: 'I ask for references to verify experience', points: 5 },
        { value: 'c', label: 'I am skeptical given the little experience', points: 0 },
        { value: 'd', label: 'I propose starting with small projects', points: 0 }
      ]
    },
    {
      id: 'q18',
      text: 'In which area would you like more training?',
      type: 'radio',
      required: true,
      excludeFromScore: true,
      options: [
        { value: 'a', label: 'Advanced technical skills in my sector', points: 0 },
        { value: 'b', label: 'Inclusive communication and diversity management', points: 0 },
        { value: 'c', label: 'Marketing and client acquisition', points: 0 },
        { value: 'd', label: 'Administrative and tax management', points: 0 }
      ]
    }
  ],
  es: [
    {
      id: 'q1',
      text: 'En tu material promocional, ¿cómo te diriges a los potenciales clientes?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Uso el masculino genérico como estándar gramatical', points: 0 },
        { value: 'b', label: 'Alterno masculino y femenino en los distintos contenidos', points: 5 },
        { value: 'c', label: 'Uso fórmulas neutras cuando es posible', points: 10 },
        { value: 'd', label: 'Siempre me dirijo en masculino por simplicidad', points: 0 }
      ]
    },
    {
      id: 'q2',
      text: 'Un cliente te muestra documentos en un idioma que no conoces. ¿Cómo procedes?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Pido documentos en español o traducidos oficialmente', points: 0 },
        { value: 'b', label: 'Intento entender juntos usando traductores online', points: 10 },
        { value: 'c', label: 'Le aconsejo volver con alguien que traduzca', points: 5 },
        { value: 'd', label: 'Rechazo el servicio por problemas burocráticos', points: 0 }
      ]
    },
    {
      id: 'q3',
      text: 'Estás seleccionando imágenes para tu sitio web profesional. ¿Cuáles eliges?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Las mejores fotos profesionales disponibles', points: 5 },
        { value: 'b', label: 'Imágenes que representan varios tipos de personas', points: 10 },
        { value: 'c', label: 'Fotos coherentes con mi mercado objetivo', points: 0 },
        { value: 'd', label: 'Prefiero gráficos e iconos sin personas', points: 0 }
      ]
    },
    {
      id: 'q4',
      text: 'Un cliente comete errores gramaticales evidentes en los emails. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Corrijo amablemente para ayudarle', points: 0 },
        { value: 'b', label: 'Respondo normalmente sin señalar nada', points: 10 },
        { value: 'c', label: 'Simplifico mi lenguaje para adaptarme', points: 5 },
        { value: 'd', label: 'Evalúo si es profesionalmente fiable', points: 0 }
      ]
    },
    {
      id: 'q5',
      text: 'Un cliente te pregunta si puedes proporcionar el servicio en formato accesible. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Explico que uso formatos estándar del sector', points: 0 },
        { value: 'b', label: 'Pregunto qué formato específico necesita', points: 10 },
        { value: 'c', label: 'Propongo un recargo por el trabajo extra', points: 5 },
        { value: 'd', label: 'Sugiero servicios especializados en accesibilidad', points: 0 }
      ]
    },
    {
      id: 'q6',
      text: 'Durante una consulta, un hombre menciona casualmente a su marido. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Continúo normalmente con la consulta', points: 10 },
        { value: 'b', label: 'Pido confirmación para asegurarme de haber entendido bien', points: 0 },
        { value: 'c', label: 'Tomo nota mentalmente para futuras comunicaciones', points: 5 },
        { value: 'd', label: 'Le felicito por la bonita relación', points: 0 }
      ]
    },
    {
      id: 'q7',
      text: 'Un cliente te avisa que podría necesitar pausas frecuentes durante la reunión. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Pregunto si hay un problema de salud', points: 0 },
        { value: 'b', label: 'Programo la reunión con margen de tiempo extra', points: 10 },
        { value: 'c', label: 'Propongo una reunión más corta', points: 5 },
        { value: 'd', label: 'Sugiero posponer cuando se sienta mejor', points: 0 }
      ]
    },
    {
      id: 'q8',
      text: 'Un colaborador comparte que está atravesando un período emocionalmente difícil. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Le aconsejo que se tome un descanso del trabajo', points: 0 },
        { value: 'b', label: 'Pregunto cómo puedo adaptar la carga de trabajo', points: 10 },
        { value: 'c', label: 'Le animo a ser fuerte y profesional', points: 0 },
        { value: 'd', label: 'Sugiero recursos de apoyo profesional', points: 5 }
      ]
    },
    {
      id: 'q9',
      text: 'Para un evento empresarial de networking de pie, ¿qué montaje eliges?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Mesas altas elegantes tipo cocktail party', points: 0 },
        { value: 'b', label: 'Mix de asientos altos y bajos, mesas y áreas lounge', points: 10 },
        { value: 'c', label: 'Recepción de pie para favorecer el movimiento', points: 0 },
        { value: 'd', label: 'Disposición tradicional con asientos asignados', points: 5 }
      ]
    },
    {
      id: 'q10',
      text: 'Un potencial cliente no puede proporcionar una dirección de residencia estable. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Pregunto si puede usar una dirección de domiciliación', points: 10 },
        { value: 'b', label: 'Propongo usar mi dirección profesional', points: 5 },
        { value: 'c', label: 'Verifico alternativas legales para la facturación', points: 10 },
        { value: 'd', label: 'Sugiero regularizar primero la situación', points: 0 }
      ]
    },
    {
      id: 'q11',
      text: '¿Cómo estructuras tus tarifas profesionales?',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Precio fijo igual para todos', points: 0 },
        { value: 'b', label: 'Tengo opciones de precio diferentes para necesidades diferentes', points: 10 },
        { value: 'c', label: 'Tarifas premium para clientes adinerados', points: 5 },
        { value: 'd', label: 'El mínimo para cubrir costes', points: 0 }
      ]
    },
    {
      id: 'q12',
      text: 'Durante una presentación, notas que alguien parece no seguir bien. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Continúo normalmente, eligieron participar', points: 0 },
        { value: 'b', label: 'Ralentizo y uso un lenguaje más sencillo', points: 10 },
        { value: 'c', label: 'Pregunto si todos están siguiendo', points: 5 },
        { value: 'd', label: 'Ignoro y procedo con quienes siguen activamente', points: 0 }
      ]
    },
    {
      id: 'q13',
      text: 'Un cliente de más de setenta años te contacta para servicios digitales. Piensas:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Tendré que simplificar mucho las explicaciones', points: 0 },
        { value: 'b', label: 'Primero verifico su nivel de competencia', points: 10 },
        { value: 'c', label: 'Propongo un paquete básico para principiantes', points: 5 },
        { value: 'd', label: 'Me preparo para repetir todo varias veces', points: 0 }
      ]
    },
    {
      id: 'q14',
      text: 'Un cliente te pide mover una cita por una festividad religiosa. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Verifico mi disponibilidad y reorganizo', points: 10 },
        { value: 'b', label: 'Propongo alternativas por teléfono o video', points: 5 },
        { value: 'c', label: 'Pido más detalles sobre la festividad para entender', points: 0 },
        { value: 'd', label: 'Acepto aplicando la política de cambios de última hora', points: 0 }
      ]
    },
    {
      id: 'q15',
      text: 'Durante una consulta descubres que el cliente tiene una estructura familiar no convencional. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Adapto mis comunicaciones a la situación específica', points: 10 },
        { value: 'b', label: 'Me atengo estrictamente al aspecto profesional', points: 5 },
        { value: 'c', label: 'Hago preguntas para comprender mejor las dinámicas', points: 0 },
        { value: 'd', label: 'Uso términos genéricos para evitar errores', points: 0 }
      ]
    },
    {
      id: 'q16',
      text: 'Un cliente de una zona rural remota te contacta. Tú:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Propongo solo servicios online por practicidad', points: 5 },
        { value: 'b', label: 'Evalúo juntos la mejor solución para ambos', points: 10 },
        { value: 'c', label: 'Aplico suplemento por distancia', points: 0 },
        { value: 'd', label: 'Sugiero profesionales más cercanos', points: 0 }
      ]
    },
    {
      id: 'q17',
      text: 'Un joven profesional te propone una colaboración innovadora. Tu primera reacción:',
      type: 'radio',
      required: true,
      options: [
        { value: 'a', label: 'Evalúo la idea independientemente de la edad', points: 10 },
        { value: 'b', label: 'Pido referencias para verificar la experiencia', points: 5 },
        { value: 'c', label: 'Soy escéptico dada la poca experiencia', points: 0 },
        { value: 'd', label: 'Propongo empezar con proyectos pequeños', points: 0 }
      ]
    },
    {
      id: 'q18',
      text: '¿En qué ámbito te gustaría más formación?',
      type: 'radio',
      required: true,
      excludeFromScore: true,
      options: [
        { value: 'a', label: 'Competencias técnicas avanzadas de mi sector', points: 0 },
        { value: 'b', label: 'Comunicación inclusiva y gestión de la diversidad', points: 0 },
        { value: 'c', label: 'Marketing y adquisición de clientes', points: 0 },
        { value: 'd', label: 'Gestión administrativa y fiscal', points: 0 }
      ]
    }
  ]
};

// Utility functions
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function getBadgeTier(score: number): 'gold' | 'silver' | null {
  if (score >= THRESHOLDS.EXCELLENT) return 'gold';
  if (score >= THRESHOLDS.APPROVED) return 'silver';
  return null;
}

export function getRetryDate(score: number): Date | null {
  // All non-approved users get 30 days cooldown
  if (score < THRESHOLDS.APPROVED) {
    const retryDate = new Date();
    retryDate.setDate(retryDate.getDate() + RETRY_DAYS.DEFAULT);
    return retryDate;
  }
  return null;
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Get status based on score (new 4-level system)
export type ScreeningStatus = 'approved' | 'pending_review' | 'on_hold';

export function getStatusFromScore(score: number): ScreeningStatus {
  if (score >= THRESHOLDS.APPROVED) return 'approved';
  if (score >= THRESHOLDS.PENDING_REVIEW) return 'pending_review';
  return 'on_hold';
}
